import { useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import {
  getFormTemplate,
  updateFormTemplate as updateFormTemplateRedux,
  updateFormTemplateText,
} from '@atom/actions/formTemplateActions';
import {
  requestSchemaTreeUpdate,
  retrieveInventorySchemas,
} from '@atom/actions/inventorySchemaActions';
import {
  DragDropContext,
  DropResult,
} from '@atom/components/common/dragAndDrop';
import { useNavigation } from '@atom/hooks/useNavigation';
import {
  addAllFormTemplateAttributes,
  addFormTemplateAssetField,
  addFormTemplateField,
  addFormTemplateModule,
  addFormTemplatePage,
  deleteFormTemplateField,
  deleteFormTemplatePage,
  duplicateFormTemplateField,
  duplicateFormTemplatePage,
  formTemplateSelector,
  publishFormTemplate,
  removeFormTemplateAssetSchema,
  updateCiFormModule,
  updateFormTemplateAttribute,
  updateFormTemplateAttributeGroup,
  updateFormTemplateFieldOrder,
  updateFormTemplateFields,
  updateFormTemplatePageName,
  updateFormTemplatePageOrder,
} from '@atom/selectors/formSelectors';
import { AttributeGroupItem, FormFieldType } from '@atom/types/form';
import {
  AttributesType,
  InventorySchemaItem,
  SchemaType,
} from '@atom/types/schema';
import { FormTemplateDetailState, RootState } from '@atom/types/store';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import FormBuilderCanvas from './FormBuilderCanvas';
import FormBuilderContext from './FormBuilderContext';
import FormBuilderHeader from './FormBuilderHeader';
import { formBuilderStateReducer, INIT_FORM_STATE } from './FormBuilderState';
import FormPages from './FormPages';
import ToolbarBase from './ToolbarBase';

import classes from './formBuilder.module.css';

const FormBuilder = () => {
  const [, { navigate, params }] = useNavigation();
  const { id } = params;
  const dispatchRedux = useDispatch();
  const [formBuilderState, dispatchState] = useReducer(
    formBuilderStateReducer,
    INIT_FORM_STATE,
  );

  const {
    isDrawerOpen,
    selectedPage,
    pageInEditMode,
    pageName,
    selectedSchema,
  } = formBuilderState;

  const formTemplate = useSelector((state: RootState) =>
    formTemplateSelector(state),
  );
  const loadingFormTemplate = useSelector(
    (state: RootState) => state.loading.loadingFormTemplate,
  );
  const loadingInventorySchemaTree = useSelector(
    (state: RootState) => state.loading.loadingInventorySchemaTree,
  );
  const loadingFormTemplateUpdate = useSelector(
    (state: RootState) => state.loading.loadingFormTemplateUpdate,
  );

  const inventorySchemas = useSelector(
    (state: RootState) => state.inventorySchemas,
  );
  const inventorySchemaTree = useSelector(
    (state: RootState) => state.inventorySchemaTree,
  );

  useEffect(() => {
    if (doesNotHaveRolePermissions(ROLE_SETS.MANAGER)) {
      navigate(`/form-builder/${id}/preview`, { replace: true });
    }

    if (isNilOrEmpty(inventorySchemas)) {
      dispatchRedux(
        retrieveInventorySchemas({
          rootSchemas: true,
        }),
      );
    }
    dispatchRedux(getFormTemplate(id));
  }, []);

  useEffect(() => {
    if (id === formTemplate?.id && formTemplate) {
      if (formTemplate.isPublished) {
        navigate(`/form-builder/${formTemplate.id}/preview`, { replace: true });
      } else {
        dispatchState({
          type: 'UPDATE_STATE',
          data: {
            formTemplate,
            selectedSchema: formTemplate.schemaId,
          },
        });
      }
    }
  }, [formTemplate]);

  const saveFormTemplate = (formTemplateNew: FormTemplateDetailState) => {
    dispatchRedux(updateFormTemplateRedux(formTemplateNew));
  };

  const toggleDrawer = () => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { isDrawerOpen: !isDrawerOpen },
    });
  };

  const updateSelectedPage = (index: number) => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { selectedPage: index, pageInEditMode: null },
    });
  };

  const updatePageInEditMode = (index: any) => {
    if (R.isNil(index)) {
      dispatchState({
        type: 'UPDATE_STATE',
        data: { pageInEditMode: index, pageName: '' },
      });
      return;
    }

    const newPageName = formTemplate.pages[index].name;
    dispatchState({
      type: 'UPDATE_STATE',
      data: {
        pageInEditMode: index,
        selectedPage: index,
        pageName: newPageName,
      },
    });
  };

  const addField = (type: string) => {
    const updatedFormTemplate = addFormTemplateField(
      type,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const addAssetField = (assetField: FormFieldType, schemaId: string) => {
    const updatedFormTemplate = addFormTemplateAssetField(
      assetField,
      selectedPage,
      formTemplate,
      schemaId,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: {
        formTemplate: updatedFormTemplate,
        selectedSchema: schemaId,
      },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const deleteField = (deleteId: string) => {
    const updatedFormTemplate = deleteFormTemplateField(
      deleteId,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const duplicateField = (duplicateId: string) => {
    const updatedFormTemplate = duplicateFormTemplateField(
      duplicateId,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const addPage = () => {
    const pageCount = R.length(formTemplate.pages);
    const updatedFormTemplate = addFormTemplatePage(formTemplate);
    const newPageName = updatedFormTemplate.pages[pageCount].name;

    dispatchState({
      type: 'UPDATE_STATE',
      data: {
        formTemplate: updatedFormTemplate,
        selectedPage,
        pageInEditMode: pageCount,
        pageName: newPageName,
      },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const deletePage = (index: number): boolean => {
    const updatedFormTemplate = deleteFormTemplatePage(index, formTemplate);

    dispatchState({
      type: 'UPDATE_STATE',
      data: {
        formTemplate: updatedFormTemplate,
        selectedPage: 0,
      },
    });

    saveFormTemplate(updatedFormTemplate);
    return true;
  };

  const duplicatePage = (index: number) => {
    const updatedFormTemplate = duplicateFormTemplatePage(index, formTemplate);
    const newSelectedPage = index + 1;
    const newPageName = updatedFormTemplate.pages[selectedPage].name;

    dispatchState({
      type: 'UPDATE_STATE',
      data: {
        formTemplate: updatedFormTemplate,
        selectedPage: newSelectedPage,
        pageInEditMode: selectedPage,
        pageName: newPageName,
      },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const updatePageName = (name: string) => {
    const updatedFormTemplate = updateFormTemplatePageName(
      name,
      pageInEditMode,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate, pageName: name },
    });
    dispatchRedux(updateFormTemplateText(updatedFormTemplate));
  };

  const updateFormTemplate = (updateId: string, data: Object) => {
    const updatedFormTemplate = updateFormTemplateFields(
      updateId,
      selectedPage,
      data,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    dispatchRedux(updateFormTemplateText(updatedFormTemplate));
  };

  const updateFieldOrder = (
    updateId: string,
    toIndex: number,
    fromIndex: number,
  ) => {
    const updatedFormTemplate = updateFormTemplateFieldOrder(
      updateId,
      toIndex,
      fromIndex,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const updatePageOrder = (toIndex: number, fromIndex: number) => {
    const updatedFormTemplate = updateFormTemplatePageOrder(
      toIndex,
      fromIndex,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const onDragEndFields = (result: DropResult) => {
    const { draggableId, source, destination } = result;

    if (!destination) {
      return;
    }

    updateFieldOrder(draggableId, destination.index, source.index);
  };

  const onDragEndPages = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    updatePageOrder(destination.index, source.index);
    updateSelectedPage(destination.index);
  };

  const onToggle = (path: any[], expanded: boolean) => {
    // Double ! is used to transform undefined into a false boolean.
    // Checking against undefined here removes the need to spread in
    // the expanded key value to every schemaTree in the reducer.

    dispatchRedux(
      requestSchemaTreeUpdate({
        path,
        expanded: !!expanded,
      }),
    );
  };

  const updateAttributeGroup = (
    fieldId: string,
    schemaId: string,
    addGroup: boolean,
    attributeGroup: AttributeGroupItem,
  ) => {
    const updatedFormTemplate = updateFormTemplateAttributeGroup(
      fieldId,
      schemaId,
      addGroup,
      attributeGroup,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const updateAttribute = (
    fieldId: string,
    schemaId: string,
    addAttribute: boolean,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => {
    const updatedFormTemplate = updateFormTemplateAttribute(
      fieldId,
      schemaId,
      addAttribute,
      attributeGroupName,
      attribute,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const addAllAttributes = (
    fieldId: string,
    schemaId: string,
    attributeGroupName: string,
    attributes: AttributesType[],
  ) => {
    const updatedFormTemplate = addAllFormTemplateAttributes(
      fieldId,
      schemaId,
      attributeGroupName,
      attributes,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const removeAssetSchema = (fieldId: string, schemaId: string) => {
    const updatedFormTemplate = removeFormTemplateAssetSchema(
      fieldId,
      schemaId,
      selectedPage,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const addFormModule = (
    type: string,
    conditionInspectionId?: string,
    schemaId?: string,
  ) => {
    const updatedFormTemplate = addFormTemplateModule(
      type,
      selectedPage,
      formTemplate,
      conditionInspectionId,
      schemaId,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: {
        formTemplate: updatedFormTemplate,
        selectedPage: selectedPage + 1,
      },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const updateCiFormInspection = (
    fieldId: string,
    conditionInspectionId: string,
  ) => {
    const updatedFormTemplate = updateCiFormModule(
      selectedPage,
      fieldId,
      conditionInspectionId,
      formTemplate,
    );

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const publishForm = () => {
    const updatedFormTemplate = publishFormTemplate(formTemplate);

    dispatchState({
      type: 'UPDATE_STATE',
      data: { formTemplate: updatedFormTemplate },
    });
    saveFormTemplate(updatedFormTemplate);
  };

  const matchedSchema = inventorySchemas.find(
    (schema: InventorySchemaItem): boolean => schema.id === selectedSchema,
  );

  const rootSchema = (matchedSchema ?? {}) as SchemaType;

  return (
    <FormBuilderContext.Provider
      value={{ ...formBuilderState, schemas: inventorySchemas }}
    >
      <FormBuilderHeader
        formTemplate={formTemplate}
        saveFormTemplate={saveFormTemplate}
        publishForm={publishForm}
      />
      <div className={classes.formEditContainer}>
        <DragDropContext onDragEnd={onDragEndPages}>
          <FormPages
            formTemplate={formTemplate}
            toggleDrawer={toggleDrawer}
            isDrawerOpen={isDrawerOpen}
            selectedPage={selectedPage}
            pageInEditMode={pageInEditMode}
            updateSelectedPage={updateSelectedPage}
            updatePageInEditMode={updatePageInEditMode}
            addPage={addPage}
            deletePage={deletePage}
            duplicatePage={duplicatePage}
            pageName={pageName}
            updatePageName={updatePageName}
          />
        </DragDropContext>
        <DragDropContext onDragEnd={onDragEndFields}>
          <div className={classes.formEditSection}>
            <ToolbarBase
              toggleDrawer={toggleDrawer}
              addField={addField}
              inventorySchemaTree={inventorySchemaTree}
              loadingFormTemplateUpdate={loadingFormTemplateUpdate}
              loadingInventorySchemaTree={loadingInventorySchemaTree}
              onToggle={onToggle}
              addAssetField={addAssetField}
              addFormModule={addFormModule}
            />
            <FormBuilderCanvas
              rootSchema={rootSchema}
              loading={loadingFormTemplate}
              updateFormTemplate={updateFormTemplate}
              deleteField={deleteField}
              duplicateField={duplicateField}
              updateAttributeGroup={updateAttributeGroup}
              updateAttribute={updateAttribute}
              addAllAttributes={addAllAttributes}
              removeAssetSchema={removeAssetSchema}
              updateCiFormInspection={updateCiFormInspection}
            />
          </div>
        </DragDropContext>
      </div>
    </FormBuilderContext.Provider>
  );
};

export default FormBuilder;
